import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  BookOpenText,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  CreditCard,
  FileText,
  GitBranch,
  GraduationCap,
  Lightbulb,
  Link2,
  ListChecks,
  Mail,
  MessageSquare,
  Network,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Target,
  Users,
  X,
} from 'lucide-react';

const GUIDE_CONTENT = {
  paiementSalaires: {
    title: "Guide d'utilisation",
    moduleLabel: 'Paiement des salaires',
    accent: '#246990',
    accentAlt: '#319190',
    icon: FileText,
    intro: "Ce guide explique le parcours complet du paiement des salaires : définir le calendrier, préparer l'état des salaires, demander la dotation, valider l'état comptable, puis ouvrir les paiements.",
    sections: [
      {
        id: 'role',
        title: 'Rôle du module',
        icon: FileText,
        summary: "Le module Paiement des salaires organise tout le circuit qui permet de passer du pointage au paiement effectif.",
        bullets: [
          "L'état des salaires est l'élément central : il rassemble les salaires fixes, les enseignants horaires et les employés hybrides sans doubler les montants.",
          "Il lit le mois choisi dans le calendrier du module et affiche le net restant à doter pour ce mois.",
          "Il sert de justificatif à la demande de dotation transmise à la caisse centrale.",
          "Selon le paramétrage, les sections Enseignants, Personnel administratif et Employés hybrides peuvent être ouvertes en consultation ou rester visuellement verrouillées tant que l'état des salaires du mois n'est pas validé.",
        ],
      },
      {
        id: 'calendrier',
        title: 'Calendrier de paie',
        icon: ClipboardCheck,
        summary: "Le calendrier de paiement définit les étapes attendues pour chaque mois salarial.",
        bullets: [
          "Les dates se configurent dans les paramètres de salaires de l'année scolaire.",
          "Le calendrier indique quand les avances, l'état des salaires, la demande de dotation, la validation centrale et les paiements doivent être faits.",
          "Le mois affiché dans l'état des salaires vient du sélecteur de mois du module.",
          "Si un mois civil n'existe pas dans le calendrier scolaire, l'état lit quand même ce mois au lieu de revenir automatiquement au premier mois scolaire.",
        ],
      },
      {
        id: 'lecture',
        title: "Lecture de l'état",
        icon: ListChecks,
        summary: "La comptabilité doit contrôler l'état des salaires avant toute demande d'argent.",
        bullets: [
          "Vérifier les pointages et les heures des enseignants à taux horaire.",
          "Vérifier les retenues de présence, les avances validées, les retenues enfants et les paiements déjà faits.",
          "Contrôler les alertes affichées sur les lignes avant de demander la dotation.",
          "Le montant important est le reste à doter : c'est le besoin financier encore à couvrir pour payer le mois.",
        ],
      },
      {
        id: 'dotation',
        title: 'Dotation centrale',
        icon: CreditCard,
        summary: "La dotation alimente le portefeuille qui servira au paiement des salaires.",
        steps: [
          "Depuis l'état des salaires, cliquer sur Demander la dotation.",
          "Le justificatif PDF de l'état est joint automatiquement à la demande.",
          "La caisse centrale contrôle la demande, puis la valide ou la refuse.",
          "Quand la demande est validée, le portefeuille est crédité pour le paiement des salaires.",
        ],
      },
      {
        id: 'validation',
        title: "Validation de l'état",
        icon: ShieldCheck,
        summary: "La validation comptable débloque les modules de paiement du mois.",
        bullets: [
          "Le bouton Valider l'état devient disponible quand les contrôles sont bons et que la dotation centrale couvre le montant restant à payer.",
          "La validation enregistre un snapshot du mois, du montant à doter et de l'effectif concerné.",
          "Si l'état change ensuite et que le montant à doter augmente, une nouvelle validation est demandée avant paiement.",
          "Tant que l'état n'est pas validé, les paiements restent bloqués. Si le verrouillage strict est désactivé, les sections restent consultables pour identifier les pointages manquants.",
        ],
      },
      {
        id: 'paiement',
        title: 'Paiement des salaires',
        icon: Target,
        summary: "Le paiement se fait seulement après dotation et validation de l'état des salaires.",
        bullets: [
          "Ouvrir ensuite la section concernée : Enseignants, Personnel administratif ou Employés hybrides.",
          "Les sections de paiement n'affichent que les enseignants, personnels administratifs ou employés hybrides ayant au moins un pointage du mois reçu en comptabilité.",
          "Si aucun pointage du mois n'a encore été transmis à la comptabilité, la section reste vide : il faut attendre l'envoi des pointages par les ressources humaines.",
          "Vérifier la fiche de paie avant de payer la personne.",
          "Le backend bloque toujours le paiement si la période, les pointages, la dotation ou la validation ne sont pas conformes, même quand les sections sont ouvertes en consultation.",
          "Chaque paiement alimente les traces de caisse et réduit le reste à payer du mois.",
        ],
      },
      {
        id: 'blocages',
        title: 'Blocages fréquents',
        icon: AlertTriangle,
        summary: "Les blocages doivent être lus depuis l'état des salaires, pas seulement depuis les cartes de paiement.",
        bullets: [
          "Pointages incomplets : finaliser ou corriger les pointages du mois, puis relire l'état.",
          "Alertes de paie : vérifier les montants, les avances ou les retenues affichées.",
          "Dotation en attente : attendre la validation de la caisse centrale.",
          "État non validé : revenir dans l'état des salaires et cliquer sur Valider l'état quand le bouton est disponible.",
        ],
      },
    ],
  },
  vieScolaire: {
    title: "Guide d'utilisation",
    moduleLabel: 'Vie scolaire',
    accent: '#246990',
    accentAlt: '#319190',
    icon: ShieldCheck,
    intro: "Ce guide explique comment lire le carnet de suivi terrain d'un élève, joindre des preuves, assurer le suivi et transmettre les informations utiles à la direction.",
    sections: [
      {
        id: 'role',
        title: 'Rôle du module',
        icon: ShieldCheck,
        summary: "Vie scolaire est le carnet de suivi quotidien des élèves.",
        bullets: [
          "Il est utilisé par la surveillance générale et les personnes qui constatent les faits sur le terrain.",
          "Chaque évènement garde le contexte, la gravité, les pièces jointes, les commentaires et l'historique.",
          "Les évènements graves, critiques ou avec suivi requis peuvent remonter dans l'outil décisionnel.",
        ],
      },
      {
        id: 'creation',
        title: 'Créer un évènement',
        icon: FileText,
        summary: "Un évènement doit raconter un fait précis et exploitable.",
        steps: [
          "Rechercher l'élève par nom, matricule ou classe.",
          "Choisir le type, la gravité, la date et le statut de départ.",
          "Rédiger une description factuelle : quoi, où, quand, qui et conséquence.",
          "Activer Suivi requis quand l'évènement doit être revu plus tard.",
          "Ajouter les pièces jointes utiles avant d'enregistrer.",
        ],
        note: "Pour une dégradation, une photo ou un justificatif est fortement recommandé afin de garder une preuve claire.",
      },
      {
        id: 'pieces',
        title: 'Photos et pièces jointes',
        icon: Camera,
        summary: "La preuve terrain est importante, surtout pendant les contrôles.",
        bullets: [
          "Le bouton Prendre une photo ouvre directement la caméra de l'appareil quand le navigateur l'autorise.",
          "Les fichiers permettent d'ajouter justificatifs, photos, documents ou comptes rendus.",
          "Une pièce jointe peut être consultée depuis le détail de l'évènement.",
        ],
      },
      {
        id: 'suivi',
        title: 'Suivre et compléter',
        icon: MessageSquare,
        summary: "Un évènement peut vivre dans le temps sans perdre son historique.",
        bullets: [
          "Ajouter une information complète le journal sans effacer le fait initial.",
          "Modifier sert à corriger ou compléter un évènement encore ouvert.",
          "Un évènement clôturé ne doit plus être modifié afin de conserver la trace validée.",
          "La clôture doit contenir la solution trouvée ou la conclusion du suivi.",
        ],
      },
      {
        id: 'decisionnel',
        title: "Lien avec l'outil décisionnel",
        icon: GitBranch,
        summary: "Les données Vie scolaire enrichissent les décisions de direction.",
        bullets: [
          "Un évènement grave, critique ou avec suivi requis devient un signal décisionnel.",
          "La direction peut traiter ce signal pour créer une décision ciblée.",
          "Les modifications faites dans Vie scolaire sont relues par l'outil décisionnel lors du chargement ou de l'actualisation.",
        ],
      },
      {
        id: 'bonnes-pratiques',
        title: 'Bonnes pratiques',
        icon: Lightbulb,
        summary: 'Quelques habitudes rendent le suivi beaucoup plus fiable.',
        bullets: [
          "Rester factuel et éviter les jugements personnels.",
          "Mettre la bonne gravité dès le départ.",
          "Joindre une photo quand un élément matériel est constaté.",
          "Clôturer uniquement quand la situation a une conclusion claire.",
        ],
      },
    ],
  },
  admission: {
    title: "Guide d'utilisation",
    moduleLabel: 'Admissions',
    accent: '#246990',
    accentAlt: '#319190',
    icon: GraduationCap,
    intro: "Ce guide donne une lecture du module Admissions : inscription, réinscription, choix de classe, seuils, familles, réductions, paiements et validation finale du dossier élève.",
    sections: [
      {
        id: 'role',
        title: 'Rôle du module',
        icon: GraduationCap,
        summary: "Admissions regroupe les deux parcours qui créent ou renouvellent le dossier scolaire d'un élève.",
        bullets: [
          "L'onglet Inscription sert à créer un nouvel élève pour l'année scolaire active.",
          "L'onglet Réinscription sert à reprendre un élève déjà connu et à préparer sa nouvelle année.",
          "Les deux parcours utilisent les classes, les frais, les familles, les réductions et les paiements configurés dans l'application.",
          "La confirmation finale enregistre le dossier et déclenche les contrôles de cohérence nécessaires.",
        ],
      },
      {
        id: 'inscription',
        title: 'Inscription',
        icon: FileText,
        summary: "L'inscription crée le dossier complet d'un nouvel élève.",
        bullets: [
          "Le parcours collecte l'identité de l'élève, les responsables, la scolarité, la famille, les frais et les documents.",
          "La classe choisie détermine les frais officiels repris dans le récapitulatif financier.",
          "Les documents et la photo complètent le dossier administratif quand ils sont disponibles.",
          "Le modal de confirmation permet de relire le dossier avant l'enregistrement définitif.",
          "Pendant la confirmation, le bouton affiche un chargement pour empêcher une double validation.",
        ],
      },
      {
        id: 'reinscription',
        title: 'Réinscription',
        icon: RefreshCw,
        summary: "La réinscription prolonge la scolarité d'un élève existant dans l'année active.",
        bullets: [
          "Le module aide à retrouver les anciens élèves et à sélectionner ceux qui doivent continuer.",
          "La classe cible peut être différente de l'ancienne classe selon le passage ou le redoublement.",
          "Les frais sont recalculés à partir de la classe cible et des règles de réduction appliquées.",
          "La réinscription familiale permet de traiter plusieurs enfants tout en gardant une lecture individuelle par élève.",
          "Le récapitulatif doit être relu avant validation, surtout quand les classes ou les réductions changent.",
        ],
      },
      {
        id: 'classes-seuils',
        title: 'Classes et seuils',
        icon: ShieldCheck,
        summary: "Le choix de classe donne aussi une information de capacité à l'utilisateur.",
        bullets: [
          "La liste des classes vient du module Classes & Matières.",
          "Lorsqu'une classe est sélectionnée, sa capacité, son effectif et ses places restantes sont affichés à titre informatif.",
          "Si le seuil est atteint, l'inscription ou la réinscription peut être bloquée avec un message clair.",
          "Selon le paramétrage de l'école, une classe annexe peut être créée pour absorber le dépassement après confirmation.",
          "Cette lecture évite d'inscrire un élève dans une classe déjà pleine sans action préalable.",
        ],
        note: "Le seuil sert donc à la fois d'information pour l'employé et de contrôle métier au moment de l'enregistrement.",
      },
      {
        id: 'familles',
        title: 'Familles et réductions',
        icon: Users,
        summary: "Les familles nombreuses et les réductions influencent le montant réellement demandé à chaque élève.",
        bullets: [
          "Un élève peut être inscrit sans famille, rattaché à une famille existante ou associé à une nouvelle famille.",
          "La réduction famille sert de règle de référence, mais elle peut être remplacée par une réduction personnalisée pour un élève.",
          "Une réduction exceptionnelle peut compléter le dossier quand elle est justifiée.",
          "En réinscription, il faut relire les réductions reprises, car deux enfants d'une même famille peuvent avoir des montants différents.",
          "Le motif de réduction aide la direction et la comptabilité à comprendre la décision prise.",
        ],
      },
      {
        id: 'paiements',
        title: 'Frais et paiements',
        icon: CreditCard,
        summary: "Les frais affichés viennent de la classe et sont ajustés par les règles appliquées au dossier.",
        bullets: [
          "Les frais d'inscription, frais mensuels et éventuelles options sont calculés avant validation.",
          "Le récapitulatif montre le total dû, le montant payé et le reste à payer.",
          "Les paiements mensuels doivent être vérifiés mois par mois avant l'enregistrement.",
          "Les montants sont exprimés en FCFA, sans décimales, pour rester cohérents avec la caisse.",
          "Après validation, les informations financières alimentent le dossier élève et les modules de paiement.",
        ],
      },
      {
        id: 'messages',
        title: 'Messages utilisateur',
        icon: Mail,
        summary: "Les retours visuels permettent à l'employé de savoir ce qui se passe pendant l'action.",
        bullets: [
          "Les actions courtes utilisent les boutons en chargement quand l'utilisateur est déjà dans un modal.",
          "Les erreurs longues, comme un seuil de classe atteint, utilisent le toast long pour garder le message lisible.",
          "Les toasts de succès confirment l'enregistrement ou l'action terminée.",
          "Un bouton en chargement ne doit pas être doublé par un toast loading au même moment, afin d'éviter une surcharge visuelle.",
        ],
      },
      {
        id: 'bonnes-pratiques',
        title: 'Bonnes pratiques',
        icon: ListChecks,
        summary: "Quelques vérifications évitent les erreurs avant de valider une admission.",
        bullets: [
          "Vérifier l'année scolaire active avant de commencer.",
          "Relire la classe sélectionnée et la capacité affichée.",
          "Contrôler les frais et les réductions avant la confirmation finale.",
          "Ne pas supposer que tous les enfants d'une famille ont la même réduction.",
          "Attendre la fin du chargement du bouton avant de quitter ou relancer une action.",
        ],
      },
    ],
  },
  outilDecisionnel: {
    title: "Guide d'utilisation",
    moduleLabel: 'Outil décisionnel',
    accent: '#7c3aed',
    accentAlt: '#6d28d9',
    icon: BrainIconFallback,
    intro: "Ce guide regroupe les explications de l'outil décisionnel : lecture du tableau, synthèse décisionnelle, lien avec Vie scolaire, validation direction et suivi des décisions.",
    sections: [
      {
        id: 'role',
        title: 'Rôle du module',
        icon: GraduationCap,
        summary: "L'outil décisionnel aide la direction à prendre une décision finale.",
        bullets: [
          "Il croise les données du Carnet d'évaluation avec les signaux de Vie scolaire.",
          "Il classe les élèves par moyenne, tendance, alertes, niveau de risque et décision suggérée.",
          "Il permet de prendre une décision globale sur l'élève ou une décision ciblée sur un problème précis.",
        ],
      },
      {
        id: 'dashboard',
        title: 'Tableau de bord',
        icon: Search,
        summary: "Le tableau de bord sert à repérer rapidement les élèves prioritaires.",
        bullets: [
          "Sélectionner une classe affiche les élèves de cette classe avec leurs données pédagogiques et Vie scolaire.",
          "Le mode global de À traiter remonte les alertes importantes même avant de choisir une classe.",
          "Les cartes colorent les catégories : excellence, satisfaisant, alerte ou difficulté.",
          "Les filtres et la recherche permettent de réduire la liste avant d'ouvrir une fiche.",
        ],
      },
      {
        id: 'calculs',
        title: 'Calculs et alertes',
        icon: AlertTriangle,
        summary: "Les indicateurs expliquent pourquoi un élève demande une attention particulière.",
        bullets: [
          "La tendance compare la dernière moyenne avec la moyenne précédente.",
          "Progression signifie une hausse au-delà du seuil de stabilité configuré.",
          "Stable signifie une variation faible autour du seuil.",
          "Régression signifie une baisse au-delà du seuil configuré.",
          "Les alertes comptent les périodes où l'élève est sous la moitié du barème : 10/20 au collège et au lycée, 5/10 au primaire.",
          "Au primaire, noté sur 10, les seuils et le seuil de stabilité réglés sur 20 sont ramenés sur 10.",
        ],
      },
      {
        id: 'synthese',
        title: 'Synthèse décisionnelle',
        icon: ClipboardCheck,
        summary: "La synthèse rassemble les signaux pour proposer une lecture globale.",
        bullets: [
          "Le score combine les données pédagogiques et les signaux Vie scolaire disponibles.",
          "L'origine des données indique si la synthèse vient du Carnet, de Vie scolaire, ou des deux.",
          "La décision suggérée est une aide : la direction reste celle qui valide la décision finale.",
          "La fiche élève donne le détail des motifs, évènements Vie scolaire et historiques de moyennes.",
        ],
      },
      {
        id: 'decisions',
        title: 'Décision direction',
        icon: Target,
        summary: "La validation transforme une analyse en décision suivie.",
        bullets: [
          "Une décision peut confirmer, modifier ou remplacer la suggestion du système.",
          "Le motif doit expliquer la décision retenue pour garder une trace claire.",
          "Les décisions ciblées permettent de traiter un problème précis sans changer toute la décision scolaire.",
          "Une décision clôturée conserve la solution trouvée dans l'historique.",
        ],
      },
      {
        id: 'suivi',
        title: 'Suivi et parents',
        icon: MessageSquare,
        summary: "Le suivi donne une continuité après la décision.",
        bullets: [
          "Les commentaires internes gardent les échanges de l'équipe.",
          "Les actions parents servent à noter les contacts, convocations ou engagements.",
          "Les évènements Vie scolaire restent visibles pour comprendre le contexte.",
          "Une actualisation permet de relire les dernières données avant une décision finale.",
        ],
      },
      {
        id: 'pdf',
        title: 'PDF et bonnes pratiques',
        icon: ListChecks,
        summary: "Le PDF formalise la décision quand elle doit être partagée ou archivée.",
        bullets: [
          "Générer le PDF seulement après avoir relu les données importantes.",
          "Vérifier les moyennes, alertes, évènements et motifs avant diffusion.",
          "Utiliser les filtres pour traiter une classe à la fois quand le volume est important.",
          "Éviter de valider une décision sans motif exploitable.",
        ],
      },
    ],
  },
  liaisonInterSites: {
    title: "Guide d'utilisation",
    moduleLabel: 'Liaison Inter-Sites',
    accent: '#246990',
    accentAlt: '#319190',
    icon: Network,
    intro: "Ce guide explique comment lire la liaison entre sites : familles inter-sites, réductions, régularisation, transferts, enfants à charge et journal Hub.",
    sections: [
      {
        id: 'role',
        title: 'Rôle du module',
        icon: Network,
        summary: "La Liaison Inter-Sites synchronise les informations utiles entre les établissements d'un même groupe.",
        bullets: [
          "Elle relie les tenants actifs via le Hub central.",
          "Elle permet de suivre les familles présentes sur plusieurs sites.",
          "Elle encadre les transferts d'élèves, d'employés et les liens d'enfants à charge.",
          "Le module donne une lecture consolidée sans remplacer les actions métier de chaque site.",
        ],
      },
      {
        id: 'familles',
        title: 'Familles inter-sites',
        icon: Users,
        summary: "Une famille inter-site existe quand au moins deux sites ont chacun un membre actif rattaché.",
        bullets: [
          "Le site de référence pilote la famille dans le Hub.",
          "Les sites distants rattachent leurs propres élèves sans modifier les dossiers des autres sites.",
          "Une famille peut sortir de l'inter-site si elle ne contient plus de membres actifs sur plusieurs sites.",
          "Les badges et la timeline aident à comprendre l'état actuel de la liaison.",
        ],
      },
      {
        id: 'reductions',
        title: 'Réductions',
        icon: ShieldCheck,
        summary: "Les réductions famille restent pilotées par le site de référence.",
        bullets: [
          "La réduction famille peut être propagée aux sites rattachés.",
          "Une réduction personnalisée reste locale à l'élève concerné.",
          "Un site distant peut rattacher un élève avec la règle famille ou une règle personnalisée selon le cas.",
          "La lecture des réductions doit se faire élève par élève.",
        ],
      },
      {
        id: 'regularisation',
        title: 'Régularisation',
        icon: AlertTriangle,
        summary: "La régularisation explique pourquoi une liaison n'est plus complète.",
        bullets: [
          "Un site peut garder la famille en local même si la liaison inter-site est sortie du Hub actif.",
          "Le compteur de délai indique quand une action est attendue avant archivage.",
          "La re-liaison ou le rattachement remet la famille dans le circuit inter-site si les conditions sont réunies.",
          "Actualiser le module permet de relire le dernier état calculé par le Hub.",
        ],
      },
      {
        id: 'transferts',
        title: 'Transferts',
        icon: ArrowRightIconFallback,
        summary: "Les transferts sont encadrés par une validation entre sites.",
        bullets: [
          "Un transfert démarre par une demande, puis suit les validations prévues.",
          "Les transferts élèves conservent l'origine du site source.",
          "Les transferts employés ou enseignants suivent leur propre circuit de validation.",
          "Le statut du transfert indique s'il est en attente, validé ou exécuté.",
        ],
      },
      {
        id: 'enfants',
        title: 'Enfants à charge',
        icon: Link2,
        summary: "Les liens d'enfants à charge permettent de gérer des retenues entre sites.",
        bullets: [
          "Une liaison active permet d'identifier un enfant scolarisé sur un autre site du groupe.",
          "Les suspensions et restaurations suivent les changements de statut détectés.",
          "Les transactions validées gardent une trace financière consultable.",
          "Les actions doivent rester cohérentes avec les dossiers locaux de chaque site.",
        ],
      },
      {
        id: 'journal',
        title: 'Journal Hub',
        icon: ClipboardCheck,
        summary: "Le journal explique les évènements synchronisés entre les sites.",
        bullets: [
          "Il conserve les actions importantes : activation, rattachement, retrait, sortie inter-site ou archivage.",
          "Il sert à comprendre l'historique, pas à modifier directement les données.",
          "Les actions métier se font depuis les modules concernés, puis le Hub conserve la trace.",
        ],
      },
      {
        id: 'bonnes-pratiques',
        title: 'Bonnes pratiques',
        icon: ListChecks,
        summary: "Quelques réflexes évitent les liaisons incomprises.",
        bullets: [
          "Avant de re-lier une famille, vérifier dans chaque tenant quel site a encore des élèves actifs.",
          "Ne pas confondre famille locale et famille inter-site : une famille locale peut avoir un seul élève actif.",
          "Quand un site affiche zéro membre, c'est ce site qui doit rattacher de nouveaux élèves si la liaison doit redevenir inter-site.",
          "Vérifier les réductions avant de rattacher : la règle personnalisée remplace la règle famille pour l'élève concerné.",
          "Actualiser le module après un transfert ou une modification de famille pour laisser le Hub recalculer l'état.",
        ],
      },
    ],
  },
  famillesNombreuses: {
    title: "Guide d'utilisation",
    moduleLabel: 'Familles nombreuses',
    accent: '#246990',
    accentAlt: '#319190',
    icon: Users,
    intro: "Ce guide explique la gestion des familles nombreuses locales et inter-sites : activation, rattachement, réductions, régularisation, archivage et restauration.",
    sections: [
      {
        id: 'role',
        title: 'Rôle du module',
        icon: Users,
        summary: "Familles nombreuses centralise les familles, leurs élèves, leurs réductions et leurs paiements.",
        bullets: [
          "Une famille locale concerne uniquement votre tenant et peut fonctionner sans liaison inter-site.",
          "Une famille inter-site relie une même famille entre plusieurs établissements du groupe.",
          "La vue détail sert à piloter les actions : modifier, ajouter un élève, activer inter-site, re-lier, rattacher au Hub, archiver ou restaurer.",
          "Le livret de caisse donne une vue consolidée des paiements de la famille quand les données sont disponibles.",
        ],
      },
      {
        id: 'vocabulaire',
        title: 'Vocabulaire',
        icon: Network,
        summary: "Ces mots expliquent qui pilote et qui suit la liaison.",
        bullets: [
          "Site de référence : site qui active la famille en inter-site. Il pilote la liaison et les réductions famille.",
          "Site distant : site qui rattache ses propres élèves à une famille déjà publiée dans le Hub.",
          "Hub : base centrale qui relie les tenants et conserve la trace des membres inter-sites.",
          "Famille locale : famille gérée seulement sur le site courant, même si elle a déjà été inter-site par le passé.",
          "Famille sortie de l'inter-site : ancienne liaison qui n'a plus assez de sites avec membres actifs pour rester inter-site.",
        ],
      },
      {
        id: 'reductions',
        title: 'Réductions',
        icon: ShieldCheck,
        summary: "Une réduction personnalisée ne doit pas cumuler avec la réduction famille.",
        bullets: [
          "Réduction famille : règle principale appliquée aux élèves qui utilisent les réductions de la famille.",
          "Réduction personnalisée : règle propre à un élève. Elle remplace la réduction famille pour cet élève.",
          "Réduction exceptionnelle : complément ponctuel autorisé, en plus de la règle principale, avec justification.",
          "En inter-site, le site de référence modifie la réduction famille et la propage aux sites rattachés.",
          "Un site distant peut rattacher un élève avec les réductions famille ou avec une règle personnalisée locale selon le cas.",
        ],
        note: "Si une famille a 2 000 FCFA de réduction mensuelle et qu'un élève a 3 000 FCFA de réduction personnalisée, le système applique 3 000 FCFA pour cet élève. Il n'applique pas 5 000 FCFA.",
      },
      {
        id: 'activer',
        title: "Activer l'inter-site",
        icon: Network,
        summary: "L'activation publie la famille dans le Hub et fait de votre site le site de référence.",
        steps: [
          "Ouvrir la famille depuis Familles nombreuses.",
          "Vérifier que la famille contient au moins deux élèves actifs sur votre site.",
          "Ouvrir le menu Actions en haut de la fiche.",
          "Choisir Activer Inter-Site puis confirmer.",
          "La famille devient visible par les autres sites du groupe, avec votre site comme référence.",
        ],
      },
      {
        id: 'rattacher',
        title: 'Rattacher des élèves',
        icon: Link2,
        summary: "Un site distant rattache ses élèves depuis sa propre base, sans modifier les élèves des autres sites.",
        steps: [
          "Ouvrir Familles nombreuses sur le site distant.",
          "Utiliser l'action de liaison ou ouvrir la famille locale à rattacher.",
          "Rechercher la famille publiée dans le Hub.",
          "Sélectionner les élèves actifs du site courant.",
          "Choisir les réductions : règle famille ou règle personnalisée de remplacement.",
          "Confirmer pour inscrire les membres dans le Hub.",
        ],
        note: "Une famille inter-site est valide seulement si au moins deux sites différents ont chacun au moins un élève actif rattaché.",
      },
      {
        id: 'regularisation',
        title: 'Régularisation',
        icon: AlertTriangle,
        summary: "La régularisation explique pourquoi une ancienne liaison n'est plus considérée comme inter-site.",
        bullets: [
          "Si votre site garde un élève actif mais que le site distant n'a plus aucun membre détecté, la famille reste locale chez vous et sort de l'inter-site.",
          "Si votre site affiche zéro membre dans cette famille, c'est votre site qui doit rattacher de nouveaux élèves pour restaurer la liaison.",
          "Deux élèves sur un seul site ne suffisent pas : il faut au moins un élève actif sur deux sites différents.",
          "Le badge À régulariser et le compteur J-7 indiquent le délai avant archivage automatique.",
          "La timeline explique le site restant, le site sans membre et la prochaine action attendue.",
        ],
      },
      {
        id: 'relier',
        title: 'Re-lier au Hub',
        icon: RotateCcw,
        summary: "La re-liaison remet une ancienne famille inter-site dans le circuit Hub.",
        steps: [
          "Ouvrir la fiche de la famille dans Familles nombreuses.",
          "Ouvrir le menu Actions.",
          "Si votre site possède la famille locale restante, choisir Re-lier au Hub Inter-Site.",
          "Si votre site était sans membre, rattacher au moins un élève actif à la famille Hub.",
          "Actualiser Liaison Inter-Sites pour vérifier que la famille redevient active.",
        ],
      },
      {
        id: 'archivage',
        title: 'Archivage',
        icon: ArchiveIconFallback,
        summary: "L'archivage conserve l'historique sans radier automatiquement les élèves.",
        bullets: [
          "Une famille locale avec un seul élève actif peut rester visible temporairement avec un délai de régularisation.",
          "Après le délai, le Hub peut archiver la famille automatiquement si aucune liaison valide n'est restaurée.",
          "L'archivage garde un snapshot des élèves, réductions et paiements.",
          "La restauration remet la famille en service avec ses informations d'origine, sauf conflit avec une autre famille active.",
          "Les paiements déjà effectués ne sont pas modifiés par l'archivage ou la restauration.",
        ],
      },
      {
        id: 'bonnes-pratiques',
        title: 'Bonnes pratiques',
        icon: ListChecks,
        summary: "Quelques réflexes rendent le parcours plus lisible pour les utilisateurs.",
        bullets: [
          "Avant de re-lier, identifier le site qui garde encore les membres et le site qui n'en a plus.",
          "Ne pas créer une deuxième famille si une ancienne famille inter-site existe déjà : utiliser la re-liaison ou le rattachement.",
          "Toujours vérifier les réductions avant de valider un rattachement.",
          "Actualiser les vues après un transfert, une radiation ou une modification de famille.",
          "Consulter Liaison Inter-Sites pour comprendre l'état, puis agir depuis Familles nombreuses.",
        ],
      },
    ],
  },
  classesMatieres: {
    title: "Guide d'utilisation",
    moduleLabel: 'Classes & Matières',
    accent: '#246990',
    accentAlt: '#319190',
    icon: BookOpenText,
    intro: "Ce guide donne une lecture du module Classes & Matières : organisation des sections, création des classes, seuils, frais, matières et liens avec les inscriptions et réinscriptions.",
    sections: [
      {
        id: 'role',
        title: 'Rôle du module',
        icon: BookOpenText,
        summary: "Classes & Matières est la base pédagogique utilisée par les admissions, les paiements et le suivi scolaire.",
        bullets: [
          "Les sections sont créées dans les paramètres, puis les classes sont créées ici pour chaque section active.",
          "Une classe porte son nom, son ordre d'affichage, ses frais, son seuil d'accueil et ses associations pédagogiques.",
          "Les matières définissent le programme disponible et les coefficients utilisés dans les modules pédagogiques.",
          "Les admissions et réinscriptions s'appuient sur cette configuration pour afficher les classes, calculer les frais et vérifier la capacité.",
        ],
      },
      {
        id: 'classes',
        title: 'Partie classes',
        icon: Users,
        summary: "La partie classes sert à structurer chaque section et à garder une vue claire des classes ouvertes.",
        bullets: [
          "Le tableau de bord affiche les sections actives et leurs classes principales.",
          "Une classe annexe est liée à une classe mère et permet d'absorber une capacité supplémentaire sans casser la structure de départ.",
          "L'ordre d'une classe sert à stabiliser son affichage dans les listes, les sélections et les parcours d'inscription.",
          "La fiche détail d'une classe regroupe les informations, les frais, les matières, les annexes et les seuils.",
          "Une classe peut être marquée comme classe sans matière lorsqu'elle ne doit pas remonter dans les alertes pédagogiques.",
        ],
      },
      {
        id: 'seuils',
        title: 'Seuils et capacité',
        icon: ShieldCheck,
        summary: "Le seuil donne à l'école une limite claire du nombre d'élèves accepté dans une classe.",
        bullets: [
          "Le seuil par défaut sert de référence, puis chaque classe peut avoir son propre seuil si nécessaire.",
          "La fiche classe affiche le remplissage, les places restantes et les alertes de capacité.",
          "Lorsqu'un employé sélectionne une classe pendant une inscription ou une réinscription, la capacité de cette classe est affichée à titre informatif.",
          "Si le seuil est atteint, l'inscription ou la réinscription peut être bloquée avec un message clair.",
          "Selon le paramétrage de l'école, le dépassement peut aussi déclencher une création d'annexe après confirmation.",
        ],
        note: "Le seuil n'est pas seulement une information visuelle : il protège aussi les flux d'admission pour éviter de remplir une classe au-delà de la limite choisie.",
      },
      {
        id: 'frais',
        title: 'Frais de classe',
        icon: CreditCard,
        summary: "Les frais portés par la classe alimentent directement les parcours financiers de l'élève.",
        bullets: [
          "Les frais d'inscription et les frais mensuels sont repris lors de la création du dossier élève.",
          "Les options comme transport ou cantine restent lisibles dans la fiche classe quand elles sont activées pour l'établissement.",
          "Les réductions famille, réductions personnalisées ou politiques d'école se calculent ensuite à partir des montants officiels de la classe.",
          "Modifier les frais d'une classe doit donc être fait avec attention, car cette information sert de référence aux admissions suivantes.",
        ],
      },
      {
        id: 'matieres',
        title: 'Partie matières',
        icon: GraduationCap,
        summary: "La partie matières décrit le programme et les classes auxquelles chaque matière est associée.",
        bullets: [
          "Chaque matière possède un nom, un code et un coefficient.",
          "Les associations indiquent dans quelles classes la matière est enseignée.",
          "Une matière non assignée reste visible dans les alertes pour rappeler qu'elle n'est rattachée à aucune classe.",
          "L'action d'association permet d'ajouter ou de retirer des classes sans modifier le nom, le code ou le coefficient de la matière.",
          "Les classes déclarées sans matière ne doivent pas être traitées comme des anomalies dans cette lecture pédagogique.",
        ],
      },
      {
        id: 'alertes',
        title: 'Alertes à traiter',
        icon: AlertTriangle,
        summary: "Les alertes servent à repérer les incohérences utiles, pas à remplacer la décision de l'utilisateur.",
        bullets: [
          "Classes sans matière signale les classes qui attendent une association pédagogique, sauf si elles sont marquées sans matière.",
          "Seuil atteint signale les classes qui ne peuvent plus recevoir d'élèves sans action de capacité.",
          "Classes presque pleines aide à anticiper avant que le blocage d'inscription arrive.",
          "Matières non assignées signale les matières créées mais pas encore reliées à une classe.",
          "Les actions sensibles passent par une confirmation et les retours utilisateur passent par les toasts du module.",
        ],
      },
      {
        id: 'bonnes-pratiques',
        title: 'Bonnes pratiques',
        icon: ListChecks,
        summary: "Quelques réflexes évitent les erreurs au moment des inscriptions, réinscriptions et bulletins.",
        bullets: [
          "Créer ou vérifier les classes avant d'ouvrir les admissions de l'année scolaire.",
          "Configurer les seuils avant les inscriptions massives pour éviter les classes surchargées.",
          "Marquer explicitement les classes sans matière quand elles n'ont pas de programme pédagogique.",
          "Associer les matières aux bonnes classes avant d'utiliser les modules de notes ou de suivi pédagogique.",
          "Contrôler les frais de classe avant de lancer les inscriptions, surtout après une modification de tarifs.",
        ],
      },
    ],
  },
};

function BrainIconFallback(props) {
  return <BookOpenText {...props} />;
}

function ArrowRightIconFallback(props) {
  return <GitBranch {...props} />;
}

function ArchiveIconFallback(props) {
  return <FileText {...props} />;
}

const GuideUtilisationDrawer = ({ isOpen, onClose, module = 'outilDecisionnel' }) => {
  const content = GUIDE_CONTENT[module] || GUIDE_CONTENT.outilDecisionnel;
  const [activeSectionId, setActiveSectionId] = useState(content.sections[0]?.id);

  const activeSection = useMemo(
    () => content.sections.find((section) => section.id === activeSectionId) || content.sections[0],
    [activeSectionId, content.sections]
  );

  useEffect(() => {
    setActiveSectionId(content.sections[0]?.id);
  }, [content.sections, module]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const HeaderIcon = content.icon;
  const SectionIcon = activeSection?.icon || BookOpenText;

  const drawer = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-start justify-end" style={{ zIndex: 9999 }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/50"
            onClick={onClose}
          />

          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28 }}
            className="relative flex h-screen w-full max-w-4xl flex-col bg-white shadow-2xl"
          >
            <div
              className="flex items-start justify-between gap-4 px-5 py-5 text-white"
              style={{ background: `linear-gradient(to right, ${content.accent}, ${content.accentAlt})` }}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-lg bg-white/15">
                  <HeaderIcon size={22} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-white/70">{content.moduleLabel}</p>
                  <h2 className="text-xl font-bold">{content.title}</h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-white/80">{content.intro}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-white/80 transition hover:bg-white/15 hover:text-white"
                aria-label="Fermer le guide"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col bg-slate-50 lg:flex-row">
              <nav className="border-b border-slate-200 bg-white p-3 lg:w-72 lg:flex-shrink-0 lg:border-b-0 lg:border-r">
                <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
                  {content.sections.map((section) => {
                    const Icon = section.icon;
                    const isActive = section.id === activeSectionId;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => setActiveSectionId(section.id)}
                        className={`flex min-w-max items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition lg:min-w-0 ${
                          isActive
                            ? 'text-white shadow-sm'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                        style={isActive ? { backgroundColor: content.accent } : undefined}
                      >
                        <Icon size={16} />
                        <span>{section.title}</span>
                      </button>
                    );
                  })}
                </div>
              </nav>

              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                <div className="mx-auto max-w-3xl space-y-5">
                  <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-start gap-3">
                      <div
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${content.accent}14`, color: content.accent }}
                      >
                        <SectionIcon size={21} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{activeSection.title}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-500">{activeSection.summary}</p>
                      </div>
                    </div>

                    {activeSection.steps && (
                      <div className="space-y-3">
                        {activeSection.steps.map((step, index) => (
                          <div key={step} className="flex gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <div
                              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                              style={{ backgroundColor: content.accent }}
                            >
                              {index + 1}
                            </div>
                            <p className="text-sm leading-6 text-slate-700">{step}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeSection.bullets && (
                      <div className="space-y-3">
                        {activeSection.bullets.map((bullet) => (
                          <div key={bullet} className="flex gap-3">
                            <CheckCircle2 className="mt-0.5 flex-shrink-0 text-emerald-500" size={17} />
                            <p className="text-sm leading-6 text-slate-700">{bullet}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeSection.note && (
                      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <div className="flex items-start gap-2">
                          <Lightbulb className="mt-0.5 flex-shrink-0 text-amber-600" size={17} />
                          <p className="text-sm leading-6 text-amber-800">{activeSection.note}</p>
                        </div>
                      </div>
                    )}
                  </section>

                  <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-600">
                    <div className="mb-2 flex items-center gap-2 font-semibold text-slate-800">
                      <RefreshCw size={16} style={{ color: content.accent }} />
                      Réflexe important
                    </div>
                    <p>
                      Quand une information vient d'être ajoutée ou modifiée, actualiser la vue avant de prendre une
                      décision finale. Le guide reste consultable sans quitter le module.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return drawer;
  return createPortal(drawer, document.body);
};

export default GuideUtilisationDrawer;
